import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload as UploadIcon,
  X as XIcon,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import FormInput from "../../../../Team/AddNutritionist/components/FormInput";
import FormDropdown from "../../../../Team/AddNutritionist/components/FormDropdown";
import { DropdownOption } from "../../../../Ui_Components/CustomDropdown";
import CustomDropdown from "../../../../Ui_Components/CustomDropdown";
import {
  DetailsNutritionData,
  DetailsNutritionErrors,
  MicroNutrientKey,
  initialMicroNutrients,
} from "../types";
import {
  checkIngredientNameExists,
  getIngredientCategories,
} from "../../../../../services/ingredients";

export interface DuplicateNameInfo {
  matchedItemName: string;
  matchedFoodId: number;
}

interface DetailsNutritionStepProps {
  data: DetailsNutritionData;
  onChange: (data: DetailsNutritionData) => void;
  errors?: DetailsNutritionErrors;
  onError?: (title: string, message?: string) => void;
  // In edit mode the current record's own name is not a duplicate — the
  // parent passes it so the check can skip identical values.
  originalName?: string;
  // In edit mode the current record's food_id is excluded from the check
  // result, so editing an ingredient without renaming it doesn't flag itself.
  currentFoodId?: number;
  // Bubbled up so the parent can block "Save & next" while a duplicate is
  // active. Cleared (null) when the name is empty, matches the original,
  // or the check passes.
  onDuplicateChange?: (info: DuplicateNameInfo | null) => void;
}

const MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB per mockup ("Max 1 MB")

const unitOptions: DropdownOption[] = [
  { value: "gm", label: "gm" },
  { value: "ml", label: "ml" },
];

// Diet type (drives the veg/non-veg icon on the ingredient card).
const typeOptions: DropdownOption[] = [
  { value: "veg", label: "Veg" },
  { value: "nonveg", label: "Non-veg" },
];

const allergenOptions: DropdownOption[] = [
  { value: "gluten", label: "Gluten" },
  { value: "lactose", label: "Lactose" },
  { value: "nuts", label: "Nuts" },
  { value: "soy", label: "Soy" },
  { value: "eggs", label: "Eggs" },
  { value: "shellfish", label: "Shellfish" },
];

// Display labels + unit for the micro-nutrient inputs. Order here mirrors the
// mockup: fat-soluble → water-soluble vitamins → macro minerals → trace minerals.
const MICRO_NUTRIENT_FIELD_DEFS: Array<{
  key: MicroNutrientKey;
  label: string;
}> = [
  { key: "vitaminA", label: "Vitamin A" },
  { key: "vitaminD", label: "Vitamin D" },
  { key: "vitaminE", label: "Vitamin E" },
  { key: "vitaminK", label: "Vitamin K" },
  { key: "vitaminC", label: "Vitamin C" },
  { key: "thiamine", label: "Thiamine (Vitamin B1)" },
  { key: "riboflavin", label: "Riboflavin (Vitamin B2)" },
  { key: "niacin", label: "Niacin (Vitamin B3)" },
  { key: "pantothenicAcid", label: "Pantothenic Acid (Vitamin B5)" },
  { key: "vitaminB6", label: "Vitamin B6" },
  { key: "biotin", label: "Biotin (Vitamin B7)" },
  { key: "folate", label: "Folate (Vitamin B9)" },
  { key: "vitaminB12", label: "Vitamin B12" },
  { key: "choline", label: "Choline" },
  { key: "calcium", label: "Calcium" },
  { key: "phosphorus", label: "Phosphorus" },
  { key: "magnesium", label: "Magnesium" },
  { key: "sodium", label: "Sodium" },
  { key: "potassium", label: "Potassium" },
  { key: "chloride", label: "Chloride" },
  { key: "iron", label: "Iron" },
  { key: "zinc", label: "Zinc" },
  { key: "copper", label: "Copper" },
  { key: "manganese", label: "Manganese" },
  { key: "selenium", label: "Selenium" },
  { key: "iodine", label: "Iodine" },
  { key: "chromium", label: "Chromium" },
  { key: "molybdenum", label: "Molybdenum" },
  { key: "fluoride", label: "Fluoride" },
];

const DetailsNutritionStep: React.FC<DetailsNutritionStepProps> = ({
  data,
  onChange,
  errors = {},
  onError,
  originalName,
  currentFoodId,
  onDuplicateChange,
}) => {
  const navigate = useNavigate();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [tagDraft, setTagDraft] = useState("");
  // Micro nutrients panel starts collapsed to match the mockup — user opens
  // it explicitly when they want to fill values.
  const [microOpen, setMicroOpen] = useState(false);

  // Duplicate-name state for the inline check. `null` means no duplicate;
  // otherwise carries what to display in the error and where the "View"
  // link should navigate.
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateNameInfo | null>(
    null,
  );
  const [checkingName, setCheckingName] = useState(false);

  // Category dropdown is backed by the admin master-list API so any additions
  // on the admin side show up here without a code change. Fetched once on
  // mount; the payload is small.
  const [categoryOptions, setCategoryOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const categories = await getIngredientCategories();
        if (cancelled) return;
        setCategoryOptions(
          categories.map((c) => ({
            value: c.category_name,
            label: c.category_name,
          })),
        );
      } catch (err) {
        if (cancelled) return;
        console.error("[DetailsNutritionStep] categories fetch failed", err);
        onError?.(
          "Could not load category options",
          err instanceof Error ? err.message : undefined,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Debounced duplicate-name check. Fires the CHECK_INGREDIENT_NAME_EXISTS
     endpoint once the user has stopped typing for 500ms. Skipped when the
     name is empty or (in edit mode) matches the record's original name.
     A response that points back at the current record (edit mode) is also
     ignored — editing an item without renaming it shouldn't flag itself. */
  useEffect(() => {
    const trimmed = data.name.trim();
    const isUnchanged =
      typeof originalName === "string" &&
      trimmed.toLowerCase() === originalName.trim().toLowerCase();
    if (!trimmed || isUnchanged) {
      setDuplicateInfo(null);
      setCheckingName(false);
      onDuplicateChange?.(null);
      return;
    }
    let cancelled = false;
    setCheckingName(true);
    const timer = setTimeout(async () => {
      try {
        const api = await checkIngredientNameExists(trimmed);
        if (cancelled) return;
        const isSuccess =
          api?.success === true ||
          api?.success === 1 ||
          api?.success === "1" ||
          api?.success === "true";
        const matchedFoodId =
          typeof api?.matched_food_id === "number"
            ? api.matched_food_id
            : Number(api?.matched_food_id);
        const isSelfMatch =
          typeof currentFoodId === "number" &&
          Number.isFinite(matchedFoodId) &&
          matchedFoodId === currentFoodId;
        if (isSuccess && api?.exists && !isSelfMatch && matchedFoodId) {
          const info: DuplicateNameInfo = {
            matchedItemName: api.matched_item_name || trimmed,
            matchedFoodId,
          };
          setDuplicateInfo(info);
          onDuplicateChange?.(info);
        } else {
          setDuplicateInfo(null);
          onDuplicateChange?.(null);
        }
      } catch (err) {
        if (cancelled) return;
        // Silent on failure — don't block the form on a network hiccup.
        console.error("[DetailsNutritionStep] name check failed", err);
        setDuplicateInfo(null);
        onDuplicateChange?.(null);
      } finally {
        if (!cancelled) setCheckingName(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.name, originalName, currentFoodId]);

  const update = (patch: Partial<DetailsNutritionData>) =>
    onChange({ ...data, ...patch });

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      onError?.("File too large", "Ingredient photo must be below 1 MB.");
      e.target.value = "";
      return;
    }
    const preview = URL.createObjectURL(file);
    update({ photo: file, photoPreview: preview });
  };

  // Fall back to [] so HMR / partial state (e.g. an in-flight form built
  // before these fields existed) doesn't crash on `.map` / `.includes`.
  const allergens = data.allergens ?? [];
  const tags = data.tags ?? [];
  const micro = data.micro ?? initialMicroNutrients;

  // The Allergens dropdown accepts multiple values but CustomDropdown is
  // single-select — collapse the current selection into a comma-separated
  // display label and use the picker to toggle values in/out.
  const allergensLabel = allergens
    .map((v) => allergenOptions.find((o) => o.value === v)?.label ?? v)
    .join(", ");

  const commitTagDraft = () => {
    const next = tagDraft.trim();
    if (!next) return;
    if (!tags.includes(next)) update({ tags: [...tags, next] });
    setTagDraft("");
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitTagDraft();
      return;
    }
    // Backspace on an empty draft removes the last pill — matches typical
    // tag-input UX so users don't have to reach for the mouse.
    if (e.key === "Backspace" && tagDraft.length === 0 && tags.length > 0) {
      update({ tags: tags.slice(0, -1) });
    }
  };

  const handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    // Pasting "a, b, c" should split into multiple pills at once.
    if (v.includes(",")) {
      const parts = v.split(",").map((p) => p.trim()).filter(Boolean);
      const merged = [...tags];
      parts.forEach((p) => {
        if (!merged.includes(p)) merged.push(p);
      });
      update({ tags: merged });
      setTagDraft("");
      return;
    }
    setTagDraft(v);
  };

  const removeTag = (tag: string) =>
    update({ tags: tags.filter((t) => t !== tag) });

  return (
    <div className="pt-4 space-y-4">
      {/* Card 1: photo + name + unit/base qty/category/type */}
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          {/* Photo upload */}
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="w-full aspect-square border-2 border-dashed border-[#D1D5DB] rounded-md flex flex-col items-center justify-center gap-2 hover:border-[#0DB5B5] transition-colors overflow-hidden"
            >
              {data.photoPreview ? (
                <img
                  src={data.photoPreview}
                  alt="Ingredient"
                  className="w-full h-full object-cover"
                />
              ) : (
                <>
                  <div className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center">
                    <UploadIcon className="w-4 h-4 text-[#6B7280]" />
                  </div>
                  <span className="text-xs 3xl:text-sm font-inter text-[#6B7280]">
                    Click to upload photo
                  </span>
                </>
              )}
            </button>
            <p className="mt-2 text-[11px] 3xl:text-xs font-inter text-[#9CA3AF]">
              JPG/PNG · Max 1 MB
            </p>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handlePhotoChange}
            />
          </div>

          {/* Right column fields */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                Ingredient name<span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={data.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="eg. Boiled potatoes"
                className={`w-full px-3 py-2 border rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] placeholder:text-[#9E9E9E] outline-none transition-colors bg-white ${
                  errors.name || duplicateInfo
                    ? "border-red-400 focus:border-red-500"
                    : "border-[#E0E0E0] focus:border-[#0DB5B5]"
                }`}
              />
              {errors.name && !duplicateInfo && (
                <p className="text-xs 3xl:text-sm text-red-500 font-inter">
                  {errors.name}
                </p>
              )}
              {duplicateInfo && (
                <p className="text-xs 3xl:text-sm font-inter">
                  <span className="text-red-500">
                    {duplicateInfo.matchedItemName} already exists.
                  </span>{" "}
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/food-library/ingredient/${duplicateInfo.matchedFoodId}`,
                      )
                    }
                    className="text-[#0DB5B5] hover:underline font-medium"
                  >
                    View
                  </button>
                </p>
              )}
              {!duplicateInfo && !errors.name && checkingName && (
                <p className="text-xs 3xl:text-sm text-[#9E9E9E] font-inter">
                  Checking...
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormDropdown
                label="Units"
                options={unitOptions}
                value={data.unit}
                onChange={(v) => update({ unit: v })}
                placeholder="eg. gm"
                error={errors.unit}
                required
              />
              <FormInput
                label="Base qty"
                value={data.baseQty}
                onChange={(v) => update({ baseQty: v.replace(/[^0-9.]/g, "") })}
                placeholder="eg. 100"
                error={errors.baseQty}
                required
              />
              <FormDropdown
                label="Category"
                options={categoryOptions}
                value={data.category}
                onChange={(v) => update({ category: v })}
                placeholder="eg. dairy"
                error={errors.category}
                required
              />
              <FormDropdown
                label="Type"
                options={typeOptions}
                value={data.type}
                onChange={(v) => update({ type: v })}
                placeholder="eg. veg"
                error={errors.type}
                required
              />
            </div>
          </div>
        </div>
      </div>

      {/* Card 2: nutrition macros + allergens */}
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6">
        <h3 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F] mb-4">
          Macro nutrients
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <FormInput
            label="Calories"
            unit="kcals"
            value={data.calories}
            onChange={(v) => update({ calories: v.replace(/[^0-9.]/g, "") })}
            placeholder="eg . 2000"
            error={errors.calories}
            required
          />
          <FormInput
            label="Adj protein"
            unit="g"
            value={data.adjProtein}
            onChange={(v) => update({ adjProtein: v.replace(/[^0-9.]/g, "") })}
            placeholder="eg. 20"
            error={errors.adjProtein}
            required
          />
          <FormInput
            label="Gross protein"
            unit="g"
            value={data.grossProtein}
            onChange={(v) => update({ grossProtein: v.replace(/[^0-9.]/g, "") })}
            placeholder="eg . 10"
            error={errors.grossProtein}
            required
          />
          <FormInput
            label="Carbs"
            unit="g"
            value={data.carbs}
            onChange={(v) => update({ carbs: v.replace(/[^0-9.]/g, "") })}
            placeholder="eg. 100"
            error={errors.carbs}
          />
          <FormInput
            label="Net Carbs"
            unit="g"
            value={data.netCarbs}
            onChange={(v) => update({ netCarbs: v.replace(/[^0-9.]/g, "") })}
            placeholder="eg. 100"
            error={errors.netCarbs}
            required
          />
          <FormInput
            label="Fat"
            unit="g"
            value={data.fat}
            onChange={(v) => update({ fat: v.replace(/[^0-9.]/g, "") })}
            placeholder="eg. 5"
            error={errors.fat}
            required
          />
          <FormInput
            label="GL"
            value={data.gl}
            onChange={(v) => update({ gl: v.replace(/[^0-9.]/g, "") })}
            placeholder="eg. 0.2"
            error={errors.gl}
            required
          />
          <FormInput
            label="Fiber"
            unit="g"
            value={data.fiber}
            onChange={(v) => update({ fiber: v.replace(/[^0-9.]/g, "") })}
            placeholder="eg. 0.2"
            error={errors.fiber}
          />

          {/* <div className="md:col-span-2 lg:col-span-4 flex flex-col gap-1.5">
            <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
              Allergens
            </label>
            <CustomDropdown
              options={allergenOptions}
              value=""
              onChange={(v) => {
                if (!v) return;
                const exists = allergens.includes(v);
                update({
                  allergens: exists
                    ? allergens.filter((a) => a !== v)
                    : [...allergens, v],
                });
              }}
              placeholder={allergensLabel || "eg. Gluten, Lactose"}
              renderOption={(opt) => (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    readOnly
                    checked={allergens.includes(opt.value)}
                    className="accent-[#0DB5B5]"
                  />
                  <span className="text-sm text-[#1F1F1F]">{opt.label}</span>
                </div>
              )}
            />
            {errors.allergens && (
              <p className="text-xs 3xl:text-sm text-red-500 font-inter">
                {errors.allergens}
              </p>
            )}
          </div> */}
        </div>
      </div>

      {/* Card 2b: micro nutrients (collapsible) */}
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6">
        <button
          type="button"
          onClick={() => setMicroOpen((v) => !v)}
          className="w-full flex items-center justify-between"
          aria-expanded={microOpen}
        >
          <h3 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F]">
            Micro nutrients
          </h3>
          {microOpen ? (
            <ChevronUp className="w-4 h-4 text-[#6B7280]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#6B7280]" />
          )}
        </button>
        {microOpen && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {MICRO_NUTRIENT_FIELD_DEFS.map((field) => (
              <FormInput
                key={field.key}
                label={field.label}
                value={micro[field.key]}
                onChange={(v) =>
                  update({
                    micro: {
                      ...micro,
                      [field.key]: v.replace(/[^0-9.]/g, ""),
                    },
                  })
                }
                placeholder="2000"
              />
            ))}
          </div>
        )}
      </div>

      {/* Card 3: tags */}
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Tags
          </label>
          <div
            className={`w-full min-h-[42px] px-3 py-1.5 border rounded-md flex flex-wrap items-center gap-2 bg-white transition-colors ${
              errors.tags
                ? "border-red-400 focus-within:border-red-500"
                : "border-[#E0E0E0] focus-within:border-[#0DB5B5]"
            }`}
            onClick={() => {
              const el = document.getElementById(
                "ingredient-tags-input",
              ) as HTMLInputElement | null;
              el?.focus();
            }}
          >
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#E8F8F8] text-[#1F1F1F] text-xs 3xl:text-sm font-inter"
              >
                {tag}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(tag);
                  }}
                  className="text-[#6B7280] hover:text-[#1F1F1F]"
                  aria-label={`Remove ${tag}`}
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
            <input
              id="ingredient-tags-input"
              type="text"
              value={tagDraft}
              onChange={handleTagChange}
              onKeyDown={handleTagKeyDown}
              onBlur={commitTagDraft}
              placeholder={
                tags.length === 0
                  ? "Add tags (separate with comma or enter)"
                  : ""
              }
              className="flex-1 min-w-[160px] py-1 text-sm 3xl:text-base font-inter text-[#1F1F1F] placeholder:text-[#9E9E9E] outline-none bg-transparent"
            />
          </div>
          {errors.tags && (
            <p className="text-xs 3xl:text-sm text-red-500 font-inter">
              {errors.tags}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailsNutritionStep;
